/**
 * File Upload Utilities
 * Pure functions for file handling, validation, and manipulation
 */

import type {
  FileUploadConfig,
  ValidationResult,
  FileWithUIState
} from '../types'

// Generate unique ID for files
export function generateFileId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).substr(2, 9)}`
}

// Create file with UI state
export function createFileWithUIState(file: File): FileWithUIState {
  const fileWithState = file as FileWithUIState
  fileWithState.id = generateFileId(file)
  fileWithState.status = 'pending'
  fileWithState.progress = 0
  return fileWithState
}

// Validate individual file
export function validateFile(file: File, config: FileUploadConfig): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  // File size validation
  if (config.maxSize && file.size > config.maxSize) {
    errors.push(`File "${file.name}" is too large. Maximum size is ${formatFileSize(config.maxSize)}.`)
  }

  if (config.minSize && file.size < config.minSize) {
    errors.push(`File "${file.name}" is too small. Minimum size is ${formatFileSize(config.minSize)}.`)
  }

  // File type validation
  if (config.accept && config.accept.length > 0) {
    const isValidType = config.accept.some(acceptedType => {
      if (acceptedType.includes('*')) {
        // Handle wildcards like "image/*"
        const baseType = acceptedType.split('/')[0]
        return file.type.startsWith(baseType + '/')
      }
      return file.type === acceptedType
    })

    if (!isValidType) {
      errors.push(`File "${file.name}" has an invalid type. Accepted types: ${config.accept.join(', ')}.`)
    }
  }

  // Empty file validation
  if (file.size === 0) {
    errors.push(`File "${file.name}" is empty.`)
  }

  // Large file warning (over 10MB)
  if (file.size > 10 * 1024 * 1024) {
    warnings.push(`File "${file.name}" is large and may take longer to upload.`)
  }

  return {
    isValid: errors.length === 0,
    file,
    errors,
    warnings
  }
}

// Validate multiple files
export function validateFiles(files: File[], config: FileUploadConfig): ValidationResult[] {
  const results: ValidationResult[] = []

  // Individual file validation
  files.forEach(file => {
    results.push(validateFile(file, config))
  })

  // Multiple files constraints
  if (config.maxFiles && files.length > config.maxFiles) {
    results.push({
      isValid: false,
      file: files[0], // Assign to first file for reference
      errors: [`Too many files selected. Maximum allowed: ${config.maxFiles}.`],
      warnings: []
    })
  }

  // Check for duplicate files
  const duplicateFiles = findDuplicateFiles(files)
  duplicateFiles.forEach(duplicateFile => {
    results.push({
      isValid: false,
      file: duplicateFile,
      errors: [`Duplicate file: "${duplicateFile.name}".`],
      warnings: []
    })
  })

  return results
}

// Find duplicate files based on name and size
export function findDuplicateFiles(files: File[]): File[] {
  const seen = new Map<string, File>()
  const duplicates: File[] = []

  files.forEach(file => {
    const key = `${file.name}-${file.size}`
    if (seen.has(key)) {
      duplicates.push(file)
    } else {
      seen.set(key, file)
    }
  })

  return duplicates
}

// Format file size for display
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

// Get file type category
export function getFileTypeCategory(file: File): 'image' | 'video' | 'audio' | 'document' | 'archive' | 'other' {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()

  if (type.startsWith('image/')) return 'image'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('audio/')) return 'audio'

  if (type.includes('pdf') ||
      type.includes('document') ||
      type.includes('spreadsheet') ||
      type.includes('presentation') ||
      name.endsWith('.doc') ||
      name.endsWith('.docx') ||
      name.endsWith('.xls') ||
      name.endsWith('.xlsx') ||
      name.endsWith('.ppt') ||
      name.endsWith('.pptx')) {
    return 'document'
  }

  if (type.includes('zip') ||
      type.includes('rar') ||
      type.includes('7z') ||
      type.includes('tar') ||
      name.endsWith('.zip') ||
      name.endsWith('.rar') ||
      name.endsWith('.7z')) {
    return 'archive'
  }

  return 'other'
}

// Get file extension
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.')
  return lastDot > 0 ? filename.slice(lastDot + 1).toLowerCase() : ''
}

// Check if file is image
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') ||
         ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(getFileExtension(file.name))
}

// Check if file can have thumbnail
export function canGenerateThumbnail(file: File): boolean {
  const category = getFileTypeCategory(file)
  return category === 'image' || category === 'video'
}

// Create preview URL for file (needs cleanup)
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file)
}

// Cleanup preview URL
export function cleanupPreviewUrl(url: string): void {
  try {
    URL.revokeObjectURL(url)
  } catch (error) {
    console.warn('Failed to cleanup preview URL:', error)
  }
}

// Convert File to base64 for thumbnails
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

// Get file icon based on type
export function getFileIcon(file: File): string {
  const category = getFileTypeCategory(file)
  const icons = {
    image: '🖼️',
    video: '🎥',
    audio: '🎵',
    document: '📄',
    archive: '📦',
    other: '📎'
  }
  return icons[category]
}

// Calculate upload speed
export function calculateUploadSpeed(bytesLoaded: number, timeElapsed: number): number {
  if (timeElapsed <= 0) return 0
  return bytesLoaded / (timeElapsed / 1000) // bytes per second
}

// Estimate time remaining
export function estimateTimeRemaining(bytesLoaded: number, bytesTotal: number, speed: number): number {
  if (speed <= 0 || bytesLoaded >= bytesTotal) return 0
  const bytesRemaining = bytesTotal - bytesLoaded
  return bytesRemaining / speed // seconds
}

// Format time duration
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  return `${Math.round(seconds / 3600)}h`
}

// Debounce function for performance
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

// Throttle function for performance
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0
  return (...args: Parameters<T>) => {
    const now = Date.now()
    if (now - lastCall >= delay) {
      lastCall = now
      func(...args)
    }
  }
}

// Array utilities for file management
export function moveArrayItem<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  const newArray = [...array]
  const item = newArray.splice(fromIndex, 1)[0]
  newArray.splice(toIndex, 0, item)
  return newArray
}

export function removeArrayItem<T>(array: T[], index: number): T[] {
  return array.filter((_, i) => i !== index)
}

export function replaceArrayItem<T>(array: T[], index: number, newItem: T): T[] {
  const newArray = [...array]
  newArray[index] = newItem
  return newArray
}